import { ArrowLeft, RotateCcw, Save, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsPlanMensuel, usePlanMensuel, useStructures } from '@/api/hooks';
import type { LignePlanMensuel } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { messageErreurFichier } from '@/components/pieces-jointes';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateHeureFr, montant } from '@/lib/utils';

const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const r2 = (n: number) => Math.round(n * 100) / 100;

function LignePlan({ l, modifiable }: { l: LignePlanMensuel; modifiable: boolean }) {
  const actions = useActionsPlanMensuel();
  const [edition, setEdition] = useState(false);
  const [valeurs, setValeurs] = useState<string[]>(l.plan.map((v) => String(v)));
  const nombres = valeurs.map((v) => Number(v.replace(',', '.')) || 0);
  const somme = r2(nombres.reduce((s, v) => s + v, 0));
  const ecart = r2(somme - l.budgetRevise);
  const enregistrer = async () => {
    try {
      await actions.enregistrer.mutateAsync({ id: l.id, mois: nombres });
      toast.success(`Plan mensuel de ${l.code} enregistré.`);
      setEdition(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const reinitialiser = async () => {
    try {
      const r = await actions.reinitialiser.mutateAsync(l.id);
      setValeurs(r.plan.map((v) => String(v)));
      toast.success(`${l.code} : répartition par défaut (1/12 par mois).`);
      setEdition(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  return (
    <TableRow className={cn(!l.coherent && 'bg-red-50')}>
      <TableCell className="whitespace-nowrap">
        <div className="font-medium">{l.code}</div>
        <div className="max-w-[18ch] truncate text-xs text-muted-foreground" title={l.libelle}>
          {l.libelle}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-1">
          {l.saisi ? <Badge variant={l.coherent ? 'outline' : 'destructive'}>{l.coherent ? 'saisi' : 'à mettre à jour'}</Badge> : <Badge variant="secondary">1/12</Badge>}
        </div>
      </TableCell>
      <TableCell className="num whitespace-nowrap text-right">{montant(l.budgetRevise)}</TableCell>
      {MOIS.map((m, i) =>
        edition ? (
          <TableCell key={m} className="p-1">
            <Input className="h-8 w-24 text-right text-xs" inputMode="decimal" value={valeurs[i]} onChange={(e) => setValeurs(valeurs.map((v, k) => (k === i ? e.target.value : v)))} aria-label={`${l.code} ${m}`} />
          </TableCell>
        ) : (
          <TableCell key={m} className="num whitespace-nowrap text-right text-xs">
            {montant(l.plan[i])}
          </TableCell>
        ),
      )}
      <TableCell className={cn('num whitespace-nowrap text-right text-xs', (edition ? ecart : l.ecart) !== 0 && 'font-semibold text-red-700')}>
        {montant(edition ? somme : l.somme)}
        {(edition ? ecart : l.ecart) !== 0 && <div>écart {montant(edition ? ecart : l.ecart)}</div>}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {modifiable &&
          (edition ? (
            <div className="flex gap-1">
              <Button size="sm" onClick={enregistrer} disabled={actions.enregistrer.isPending || ecart !== 0} title={ecart !== 0 ? 'La somme doit être égale au budget révisé' : 'Enregistrer'}>
                <Save className="size-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setValeurs(l.plan.map((v) => String(v))); setEdition(false); }}>
                Annuler
              </Button>
            </div>
          ) : (
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => setEdition(true)}>
                Modifier
              </Button>
              {l.saisi && (
                <Button size="sm" variant="ghost" onClick={reinitialiser} title="Revenir à 1/12 par mois" aria-label="Réinitialiser">
                  <RotateCcw className="size-4" />
                </Button>
              )}
            </div>
          ))}
        {l.modifieLe && <div className="text-xs text-muted-foreground">{l.modifiePar} · {dateHeureFr(l.modifieLe)}</div>}
      </TableCell>
    </TableRow>
  );
}

/** Plan de décaissement mensuel par ligne (cahier des charges du tableau de bord, § 6 et 10) : saisie par la DF, grille Excel, 1/12 par défaut. */
export function PlanMensuel() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const modifiable = profils.some((p) => ['DIRECTEUR_FINANCIER', 'CONTROLE_GESTION', 'ADMINISTRATEUR'].includes(p));
  const exercice = new Date().getFullYear();
  const [structure, setStructure] = useState('');
  const structures = useStructures();
  const { data, isLoading, error } = usePlanMensuel(exercice, structure || undefined);
  const actions = useActionsPlanMensuel();
  const fichier = useRef<HTMLInputElement | null>(null);
  const importer = async (f: File | null) => {
    if (!f) return;
    try {
      const r = await actions.importer.mutateAsync({ exercice, fichier: f });
      toast.success(`${r.enregistrees} plan(s) enregistré(s), ${r.inchangees} inchangé(s) sur ${r.lignesLues} ligne(s) lue(s).`, { description: r.erreurs.length ? `${r.erreurs.length} ligne(s) refusée(s) : ${r.erreurs.map((e) => `${e.code} (${e.message})`).join(' ; ')}` : undefined });
    } catch (e) {
      toast.error(messageErreurFichier(e));
    } finally {
      if (fichier.current) fichier.current.value = '';
    }
  };
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/budget" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Suivi budgétaire
          </Link>
        }
        titre={`Plan de décaissement mensuel ${exercice}`}
        sousTitre="Répartition mensuelle prévue du budget révisé de chaque ligne ; sans saisie, un douzième par mois. Le prévu des rapports d'exécution en découle."
        actions={
          <>
            <SelectNatif value={structure} onChange={(e) => setStructure(e.target.value)} aria-label="Structure">
              <option value="">Toutes les structures</option>
              {(structures.data ?? []).map((s) => (
                <option key={s.id} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
            <BoutonExcel chemin={`/budget/plan-mensuel.xlsx?exercice=${exercice}${structure ? `&structure=${structure}` : ''}`} nom={`plan-mensuel-${exercice}${structure ? `-${structure}` : ''}.xlsx`} libelle="Grille Excel" taille="default" />
            {modifiable && (
              <>
                <input ref={fichier} type="file" accept=".xlsx" className="hidden" onChange={(e) => importer(e.target.files?.[0] ?? null)} />
                <Button variant="outline" onClick={() => fichier.current?.click()} disabled={actions.importer.isPending}>
                  <Upload className="size-4" /> {actions.importer.isPending ? 'Import…' : 'Importer une grille'}
                </Button>
              </>
            )}
          </>
        }
      />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Budget révisé" valeur={montant(data.totaux.budgetRevise)} detail={`${data.lignes.length} ligne(s)`} />
            <Kpi label="Plans saisis" valeur={data.totaux.saisies} detail={`${data.lignes.length - data.totaux.saisies} à 1/12 par défaut`} />
            <Kpi label="Plans à mettre à jour" valeur={data.totaux.incoherentes} detail="somme différente du budget révisé (après virement)" accent={data.totaux.incoherentes > 0 ? 'critique' : 'ok'} />
            <Kpi label="Prévu du mois en cours" valeur={montant(data.totaux.plan[new Date().getMonth()] ?? 0)} detail={MOIS[new Date().getMonth()]} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Grille mensuelle</CardTitle>
              <CardDescription>{modifiable ? 'Modifiez une ligne puis enregistrez : la somme des douze mois doit être égale au budget révisé. Vous pouvez aussi exporter la grille, la compléter dans Excel et la réimporter.' : 'Consultation ; la saisie est réservée à la Direction financière et au contrôle de gestion.'}</CardDescription>
            </CardHeader>
            <CardContent>
              {data.lignes.length === 0 ? (
                <Vide message="Aucune ligne budgétaire pour cet exercice." />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ligne</TableHead>
                        <TableHead className="text-right">Budget révisé</TableHead>
                        {MOIS.map((m) => (
                          <TableHead key={m} className="text-right">
                            {m}
                          </TableHead>
                        ))}
                        <TableHead className="text-right">Somme</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.lignes.map((l) => (
                        <LignePlan key={`${l.id}-${l.modifieLe ?? ''}`} l={l} modifiable={modifiable} />
                      ))}
                      <TableRow className="font-semibold">
                        <TableCell>Total</TableCell>
                        <TableCell className="num text-right">{montant(data.totaux.budgetRevise)}</TableCell>
                        {data.totaux.plan.map((v, i) => (
                          <TableCell key={i} className="num whitespace-nowrap text-right text-xs">
                            {montant(v)}
                          </TableCell>
                        ))}
                        <TableCell className="num text-right text-xs">{montant(data.totaux.plan.reduce((s, v) => s + v, 0))}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
